// ════════════════════════════════════════════════════════════════════════
//  The trainer — composition only.
//
//  All decisions live in useTrainer; everything visible lives in the small
//  components next to it. This file just picks the view for the current phase.
// ════════════════════════════════════════════════════════════════════════

import type { Bundle, Lesson, StageResult } from "@otto/lehrgang";
import { cssVar } from "../../../shared/lib/cssVar.js";
import { LessonDone } from "./LessonDone.js";
import { LessonIntro } from "./LessonIntro.js";
import { PracticeCard } from "./PracticeCard.js";
import { TrainerHeader } from "./TrainerHeader.js";
import { useTrainer } from "../hooks/useTrainer.js";
import styles from "./practice.module.css";

export function Trainer({
  bundle,
  lesson,
  nextLesson,
  onBack,
  onSwitchLesson,
  onStageDone,
}: {
  bundle: Bundle;
  lesson: Lesson;
  /** For "on to lesson N" at the end — null when this is the last one. */
  nextLesson?: Lesson | null;
  onBack: () => void;
  onSwitchLesson?: (lesson: Lesson) => void;
  /** Called exactly once per finished stage — the hook for saving. */
  onStageDone?: (result: StageResult) => void;
}) {
  const trainer = useTrainer({ bundle, lesson, onStageDone });
  const { phase, stage, stageIndex, results } = trainer;

  const colorVars = cssVar({
    "--lesson-color": lesson.color,
    ...(trainer.finger ? { "--finger-color": trainer.finger.color } : {}),
  });

  return (
    <div className={styles.column} style={colorVars}>
      <TrainerHeader
        lesson={lesson}
        title={phase.name === "practice" ? stage.title : lesson.subtitle}
        doneCount={results.length}
        currentIndex={phase.name === "practice" ? stageIndex : null}
        allDone={phase.name === "done"}
        onBack={onBack}
      />

      {phase.name === "intro" && (
        <LessonIntro bundle={bundle} lesson={lesson} onStart={trainer.begin} />
      )}

      {phase.name === "practice" && (
        <PracticeCard
          bundle={bundle}
          lesson={lesson}
          stage={stage}
          state={phase.state}
          expected={trainer.expected}
          lastKey={trainer.lastKey}
          missCount={trainer.missCount}
          shaking={trainer.shaking}
          onShakeEnd={trainer.clearShake}
          fingerName={trainer.finger?.name ?? null}
          onKey={trainer.handleKey}
          wayHint={trainer.wayHint}
        />
      )}

      {phase.name === "done" && (
        <LessonDone
          bundle={bundle}
          lesson={lesson}
          results={results}
          nextLesson={nextLesson ?? null}
          onRestart={trainer.restart}
          onSwitchLesson={onSwitchLesson}
          onBack={onBack}
        />
      )}
    </div>
  );
}
